import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import * as API from "services/api"
import {
  difference, isNil, sortBy, union, cloneDeep,
} from "lodash-es"
import paginatedResourceList from "utils/paginatedResourceList"
import { errorToast } from "shared/toast"
import { getGroupingMethod } from "views/FormSubmissionList/submissionGroupingHelper"
import {
  createReportableFieldColumns,
  setReportableAnswersOnSubmissions,
} from "views/FormSubmissionList/submissionReportableFieldHelper"
import {
  getDataSortingMethod, CONVERSATIONS_CLOSED_TYPE, DATE_TYPE, FLAGGED_TYPE, FORM_TYPE, STATUS_TYPE,
  TASKS_COMPLETED_TYPE, TEXT_TYPE,
} from "shared/Table/TableDataElement/tableDataType"
import { AUTHENTICATED_FORMS_FEATURE_NAME } from "utils/addOnFeatureHelpers"
import { subscribedToAddOnFeature } from "reduxSlices/addOnFeatureSubscriptionsSlice"
import { CONVERSATIONS } from "utils/featuresHelpers"
import { isFeatureEnabled } from "reduxSlices/featuresSlice"
import {
  BATCH_SENT_AT,
  CONVERSATIONS_COUNT,
  CREATED_AT,
  FORM,
  IMPORTANT,
  SLUG,
  STATUS_NAME,
  SUBMITTER_FULL_NAME,
  TASKS_COUNT,
  UPDATED_AT,
} from "utils/formSubmissionIndexHelpers"

const SLICE_NAME = "formSubmissionTable"

const SLUG_COLUMN = {
  label: "ID",
  value: SLUG,
  sortable: true,
  width: "130px",
  dataType: TEXT_TYPE,
}

const FLAGGED_COLUMN = {
  label: "Flagged",
  value: IMPORTANT,
  sortable: true,
  width: "50px",
  dataType: FLAGGED_TYPE,
}

const FORM_COLUMN = {
  label: "Form",
  value: FORM,
  sortable: true,
  width: "350px",
  dataType: FORM_TYPE,
}

const SUBMISSION_DATE_COLUMN = {
  label: "Submission Date",
  value: CREATED_AT,
  sortable: true,
  width: "90px",
  dataType: DATE_TYPE,
}

const SUBMISSION_UPDATED_COLUMN = {
  label: "Updated Date",
  value: UPDATED_AT,
  sortable: true,
  width: "90px",
  dataType: DATE_TYPE,
}

const SURVEY_SEND_DATE_COLUMN = {
  label: "Survey Date",
  value: BATCH_SENT_AT,
  sortable: true,
  width: "90px",
  dataType: DATE_TYPE,
}

const STATUS_COLUMN = {
  label: "Status",
  value: STATUS_NAME,
  sortable: true,
  width: "150px",
  dataType: STATUS_TYPE,
}

const SUBMITTER_COLUMN = {
  label: "Submitter",
  value: SUBMITTER_FULL_NAME,
  sortable: true,
  width: "150px",
  dataType: TEXT_TYPE,
}

const TASKS_COMPLETED_COLUMN = {
  label: "Tasks Completed",
  value: TASKS_COUNT,
  sortable: true,
  width: "50px",
  dataType: TASKS_COMPLETED_TYPE,
}

const CONVERSATIONS_CLOSED_COLUMN = {
  label: "Conversations Closed",
  value: CONVERSATIONS_COUNT,
  sortable: true,
  width: "50px",
  dataType: CONVERSATIONS_CLOSED_TYPE,
}

const DEFAULT_COLUMNS = [
  STATUS_COLUMN,
  SLUG_COLUMN,
  FORM_COLUMN,
  SUBMITTER_COLUMN,
  SUBMISSION_DATE_COLUMN,
  SUBMISSION_UPDATED_COLUMN,
  SURVEY_SEND_DATE_COLUMN,
  FLAGGED_COLUMN,
  TASKS_COMPLETED_COLUMN,
  CONVERSATIONS_CLOSED_COLUMN,
]

const DEFAULT_VISIBLE_COLUMNS = [
  STATUS_COLUMN.value,
  SLUG_COLUMN.value,
  FORM_COLUMN.value,
  SUBMISSION_DATE_COLUMN.value,
  SURVEY_SEND_DATE_COLUMN.value,
  FLAGGED_COLUMN.value,
]

const DEFAULT_COLUMN_SORT = {
  column: SLUG_COLUMN.value,
  isAsc: true,
}

const SURVEY_RELATED_COLUMN_VALUES = [
  BATCH_SENT_AT,
]

const AUTHENTICATED_FORMS_COLUMN_VALUES = [
  SUBMITTER_FULL_NAME,
]

const CONVERSATIONS_COLUMN_VALUES = [
  CONVERSATIONS_COUNT,
]

const SHOW_OPEN_SUBMISSIONS = "show open"
export const SHOW_CLOSED_SUBMISSIONS = "show closed"
export const SHOW_DISCARDED_SUBMISSIONS = "show discarded"

const INITIAL_STATE = {
  allDepartmentIds: [],
  forms: {},
  formSubmissions: {},
  formSubmissionCounts: {},
  selectedCategorySlug: undefined,
  categories: [],
  categorySubmissionStatuses: [],
  columns: DEFAULT_COLUMNS,
  columnOrder: DEFAULT_COLUMNS.map((column) => column.value),
  currentSort: DEFAULT_COLUMN_SORT,
  departmentIdsToHide: [],
  facilityIdMap: {},
  formIdsToHide: [],
  groupBy: STATUS_COLUMN.value,
  show: SHOW_OPEN_SUBMISSIONS,
  isLoading: true,
  noLocationAnswerOption: true,
  paginationLoadedCount: 0,
  paginationTotalCount: 0,
  selectedDepartmentIds: [],
  visibleColumns: DEFAULT_VISIBLE_COLUMNS,
}

let incrementPaginationLoadedCount
let resetPaginationCounts
let setPaginationTotalCount

const requestFormSubmissionsApiFunction = ({ show }) => {
  switch (show) {
  case SHOW_CLOSED_SUBMISSIONS:
    return API.categoryClosedFormSubmissions
  case SHOW_DISCARDED_SUBMISSIONS:
    return API.categoryRecoverableFormSubmissions
  default:
    return API.categoryOpenFormSubmissions
  }
}

const requestFormSubmissionsFunction = ({ onPageLoad, show }) => paginatedResourceList({
  apiFunction: requestFormSubmissionsApiFunction({ show }),
  onPageLoad,
})

const loadFormSubmissions = async ({
  categorySlug, dispatch, formSubmissions, show,
}) => {
  dispatch(resetPaginationCounts())
  let categoryFormSubmissions = formSubmissions[categorySlug]?.[show]

  if (!categoryFormSubmissions) {
    const updateLoadingCounts = ({ paginationHeaders, pageResourceCount }) => {
      if (isNil(paginationHeaders.page)) return

      dispatch(setPaginationTotalCount(paginationHeaders.totalCount))
      dispatch(incrementPaginationLoadedCount(pageResourceCount))
    }

    const load = requestFormSubmissionsFunction({ onPageLoad: updateLoadingCounts, show })
    const submissions = await load({ categorySlug })

    categoryFormSubmissions = setReportableAnswersOnSubmissions(submissions)
  }

  return categoryFormSubmissions
}

const requestFormSubmissionCountsFunction = ({ show }) => {
  switch (show) {
  case SHOW_CLOSED_SUBMISSIONS:
    return API.closedFormSubmissionCounts
  case SHOW_DISCARDED_SUBMISSIONS:
    return API.recoverableFormSubmissionCounts
  default:
    return API.openFormSubmissionCounts
  }
}

const loadFormSubmissionCounts = async ({ formSubmissionCounts, show }) => {
  let shownFormSubmissionCounts = formSubmissionCounts[show]

  if (!shownFormSubmissionCounts) {
    const formSubmissionCountsResponse = await requestFormSubmissionCountsFunction({ show })()

    if (!formSubmissionCountsResponse.ok) {
      throw new Error("Unable to load form submission counts")
    }

    shownFormSubmissionCounts = formSubmissionCountsResponse.data
  }

  return shownFormSubmissionCounts
}

const buildFacilityIdMap = (facilities = []) => cloneDeep(facilities).reduce((map, facility) => {
  map[facility.id] = facility
  return map
}, {})

const buildAllDepartmentIds = (facilities = []) => Object.values(facilities).flatMap(
  (facility) => facility.departments.map((d) => d.id),
)

// When switching solutions we need to fetch
//   1.  Form submissions for the new solution
//   2.  Public forms for the new solution
//   3.  Reportable fields for the new solution
//
// Once we've fetched that data, then we update the redux state.
// See the extraReducers passed to createSlice to see how
// loading, fulfilled, and rejected are handled.
export const switchSolution = createAsyncThunk(
  `${SLICE_NAME}/switchSolution`,
  async ({ categorySlug }, thunkAPI) => {
    if (!categorySlug) {
      return {
        categorySlug: undefined,
        forms: [],
        formSubmissions: [],
        reportableFields: [],
        statusList: [],
      }
    }

    const { dispatch, getState } = thunkAPI

    const state = getState()

    const {
      categories, formSubmissionCounts, forms, formSubmissions, show,
    } = state[SLICE_NAME]

    const subscribedToAuthenticatedForms = subscribedToAddOnFeature(AUTHENTICATED_FORMS_FEATURE_NAME)(state)
    const conversationsFeatureEnabled = isFeatureEnabled(CONVERSATIONS)(state)

    const newSelectedCategory = categories.find((category) => category.slug === categorySlug)
    const { usedForSurvey } = newSelectedCategory

    try {
      // 1. Fetch form submissions
      const categoryFormSubmissions = await loadFormSubmissions({
        categorySlug, dispatch, formSubmissions, show,
      })

      // 2. Fetch form submission counts
      const shownFormSubmissionCounts = await loadFormSubmissionCounts({ formSubmissionCounts, show })

      // 3. Fetch forms
      let categoryForms = forms[categorySlug]

      if (!categoryForms) {
        let formsResponse
        if (usedForSurvey) {
          formsResponse = await API.categorySurveyForms({ categorySlug })
        } else {
          formsResponse = await API.getCategoryForms({ categorySlug })
        }

        if (!formsResponse.ok) {
          throw new Error("Unable to load forms.")
        }

        categoryForms = formsResponse.data
      }

      // 4. Fetch reportable fields
      const reportableFieldsResponse = await API.getCategoryReportableFields({ categorySlug })
      if (!reportableFieldsResponse.ok) {
        throw new Error("Unable to load forms.")
      }

      const reportableFields = reportableFieldsResponse.data

      // 5. Fetch the solution's status list
      const statusListResponse = await API.statusList({ categorySlug })
      if (!statusListResponse.ok) {
        throw new Error("Unable to load status list.")
      }

      const statusList = statusListResponse.data

      return {
        categoryForms,
        categoryFormSubmissions,
        categorySlug,
        conversationsFeatureEnabled,
        reportableFields,
        shownFormSubmissionCounts,
        statusList,
        subscribedToAuthenticatedForms,
        usedForSurvey,
      }
    } catch (error) {
      throw new Error(`Something went wrong. ${error.message}`)
    }
  },
)

export const showClosedSubmissions = createAsyncThunk(
  `${SLICE_NAME}/showClosedSubmissions`,
  async (_action, thunkAPI) => {
    const { dispatch, getState } = thunkAPI
    const { formSubmissionCounts, formSubmissions, selectedCategorySlug } = getState()[SLICE_NAME]

    const categoryFormSubmissions = await loadFormSubmissions({
      categorySlug: selectedCategorySlug,
      dispatch,
      formSubmissions,
      show: SHOW_CLOSED_SUBMISSIONS,
    })

    const shownFormSubmissionCounts = await loadFormSubmissionCounts({
      formSubmissionCounts,
      show: SHOW_CLOSED_SUBMISSIONS,
    })

    return { categoryFormSubmissions, shownFormSubmissionCounts }
  },
)

export const showDiscardedSubmissions = createAsyncThunk(
  `${SLICE_NAME}/showDiscardedSubmissions`,
  async (_action, thunkAPI) => {
    const { dispatch, getState } = thunkAPI
    const { formSubmissionCounts, formSubmissions, selectedCategorySlug } = getState()[SLICE_NAME]

    const categoryFormSubmissions = await loadFormSubmissions({
      categorySlug: selectedCategorySlug,
      dispatch,
      formSubmissions,
      show: SHOW_DISCARDED_SUBMISSIONS,
    })

    const shownFormSubmissionCounts = await loadFormSubmissionCounts({
      formSubmissionCounts,
      show: SHOW_DISCARDED_SUBMISSIONS,
    })

    return { categoryFormSubmissions, shownFormSubmissionCounts }
  },
)

export const showOpenSubmissions = createAsyncThunk(
  `${SLICE_NAME}/showOpenSubmissions`,
  async (_action, thunkAPI) => {
    const { dispatch, getState } = thunkAPI
    const { formSubmissionCounts, formSubmissions, selectedCategorySlug } = getState()[SLICE_NAME]

    const categoryFormSubmissions = await loadFormSubmissions({
      categorySlug: selectedCategorySlug,
      dispatch,
      formSubmissions,
      show: SHOW_OPEN_SUBMISSIONS,
    })

    const shownFormSubmissionCounts = await loadFormSubmissionCounts({
      formSubmissionCounts,
      show: SHOW_OPEN_SUBMISSIONS,
    })

    return { categoryFormSubmissions, shownFormSubmissionCounts }
  },
)

export const formSubmissionTableSlice = createSlice({
  name: SLICE_NAME,
  initialState: INITIAL_STATE,
  reducers: {
    clearFormSubmissionCounts: (state) => {
      state.formSubmissionCounts = {}
    },
    clearFormSubmissions: (state) => {
      state.formSubmissions = {}
    },
    hideAllForms: (state) => {
      const { formIdsToHide, forms, selectedCategorySlug } = state
      const formIds = forms[selectedCategorySlug].map((form) => form.id)

      state.formIdsToHide = union(formIdsToHide, formIds)
    },
    hideColumn: (state, action) => {
      state.visibleColumns = state.visibleColumns.filter((value) => value !== action.payload)
    },
    hideDepartment: (state, action) => {
      state.departmentIdsToHide.push(action.payload)
    },
    hideForm: (state, action) => {
      state.formIdsToHide.push(action.payload)
    },
    incrementPaginationLoadedCount: (state, action) => {
      const { paginationLoadedCount } = state
      const newCount = paginationLoadedCount + action.payload
      state.paginationLoadedCount = newCount
    },
    reorderColumns: (state, action) => {
      const { startIndex, endIndex } = action.payload

      const orderedColumns = sortBy(state.columns, [(column) => state.columnOrder.indexOf(column.value)])

      const valueToMove = orderedColumns[startIndex].value
      const valueAtDesiredIndex = orderedColumns[endIndex].value

      const desiredIndexInColumnOrder = state.columnOrder.indexOf(valueAtDesiredIndex)

      const newColumnOrder = state.columnOrder.filter((value) => value !== valueToMove)
      newColumnOrder.splice(desiredIndexInColumnOrder, 0, valueToMove)

      state.columnOrder = newColumnOrder
    },
    resetPaginationCounts: (state) => {
      state.paginationLoadedCount = 0
      state.paginationTotalCount = 0
    },
    setCategories: (state, action) => {
      state.categories = action.payload
    },
    setCurrentSort: (state, action) => {
      state.currentSort = action.payload
    },
    setColumnOrder: (state, action) => {
      state.columnOrder = action.payload
    },
    setFacilitiesForFormSubmissions: (state, action) => {
      const { payload: newFacilities } = action

      state.facilityIdMap = buildFacilityIdMap(newFacilities)
      state.allDepartmentIds = buildAllDepartmentIds(newFacilities)
    },
    setFormIdsToHide: (state, action) => {
      state.formIdsToHide = action.payload
    },
    setDepartmentIdsToHide: (state, action) => {
      state.departmentIdsToHide = action.payload
    },
    setNoLocationAnswerOption: (state, action) => {
      state.noLocationAnswerOption = action.payload
    },
    setPaginationCurrentCount: (state, action) => {
      state.paginationLoadedCount = action.payload
    },
    setPaginationTotalCount: (state, action) => {
      state.paginationTotalCount = action.payload
    },
    setSelectedGrouping: (state, action) => {
      state.groupBy = action.payload
    },
    setShow: (state, action) => {
      state.show = action.payload
    },
    setVisibleColumns: (state, action) => {
      state.visibleColumns = action.payload
    },
    showAllForms: (state) => {
      const { formIdsToHide, forms, selectedCategorySlug } = state
      const formIds = forms[selectedCategorySlug].map((form) => form.id)

      state.formIdsToHide = difference(formIdsToHide, formIds)
    },
    showColumn: (state, action) => {
      state.visibleColumns.push(action.payload)
    },
    showDepartment: (state, action) => {
      state.departmentIdsToHide = state.departmentIdsToHide.filter((id) => id !== action.payload)
    },
    showForm: (state, action) => {
      state.formIdsToHide = state.formIdsToHide.filter((id) => id !== action.payload)
    },
    sortByColumn: (state, action) => {
      const { column: oldSortColumn, isAsc: currentSortOrder } = state.currentSort
      const { payload: newSortColumn } = action

      state.currentSort = {
        column: newSortColumn,
        isAsc: oldSortColumn === newSortColumn ? !currentSortOrder : true,
      }
    },
    hideAllDepartmentIds(state) {
      state.departmentIdsToHide = state.allDepartmentIds
    },
    showAllDepartmentIds(state) {
      state.departmentIdsToHide = []
    },
    toggleFacilityVisibility(state, action) {
      const facility = action.payload
      const deptIds = facility.departments?.map((d) => d.id) || []

      const deptSet = new Set(deptIds)
      const hiddenSet = new Set(state.departmentIdsToHide)
      const isHidden = deptIds.some((id) => hiddenSet.has(id))

      if (isHidden) {
        state.departmentIdsToHide = state.departmentIdsToHide.filter(
          (id) => !deptSet.has(id),
        )
      } else {
        deptIds.forEach((id) => hiddenSet.add(id))
        state.departmentIdsToHide = [...hiddenSet]
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(showClosedSubmissions.pending, (state) => {
        state.isLoading = true
      })
      .addCase(showClosedSubmissions.fulfilled, (state, action) => {
        const { selectedCategorySlug } = state
        const { categoryFormSubmissions, shownFormSubmissionCounts } = action.payload

        if (!state.formSubmissions[selectedCategorySlug]) {
          state.formSubmissions[selectedCategorySlug] = {}
        }

        state.formSubmissions[selectedCategorySlug][SHOW_CLOSED_SUBMISSIONS] = categoryFormSubmissions
        state.show = SHOW_CLOSED_SUBMISSIONS
        state.formSubmissionCounts[SHOW_CLOSED_SUBMISSIONS] = shownFormSubmissionCounts
        state.isLoading = false
      })
      .addCase(showClosedSubmissions.rejected, (state, action) => {
        errorToast(action.error.message)
        state.isLoading = false
      })
      .addCase(showDiscardedSubmissions.pending, (state) => {
        state.isLoading = true
      })
      .addCase(showDiscardedSubmissions.fulfilled, (state, action) => {
        const { selectedCategorySlug } = state
        const { categoryFormSubmissions, shownFormSubmissionCounts } = action.payload

        if (!state.formSubmissions[selectedCategorySlug]) {
          state.formSubmissions[selectedCategorySlug] = {}
        }

        state.formSubmissions[selectedCategorySlug][SHOW_DISCARDED_SUBMISSIONS] = categoryFormSubmissions
        state.show = SHOW_DISCARDED_SUBMISSIONS
        state.formSubmissionCounts[SHOW_DISCARDED_SUBMISSIONS] = shownFormSubmissionCounts
        state.isLoading = false
      })
      .addCase(showDiscardedSubmissions.rejected, (state, action) => {
        errorToast(action.error.message)
        state.isLoading = false
      })
      .addCase(showOpenSubmissions.pending, (state) => {
        state.isLoading = true
      })
      .addCase(showOpenSubmissions.fulfilled, (state, action) => {
        const { selectedCategorySlug } = state
        const { categoryFormSubmissions, shownFormSubmissionCounts } = action.payload

        if (!state.formSubmissions[selectedCategorySlug]) {
          state.formSubmissions[selectedCategorySlug] = {}
        }

        state.formSubmissions[selectedCategorySlug][SHOW_OPEN_SUBMISSIONS] = categoryFormSubmissions
        state.show = SHOW_OPEN_SUBMISSIONS
        state.formSubmissionCounts[SHOW_OPEN_SUBMISSIONS] = shownFormSubmissionCounts
        state.isLoading = false
      })
      .addCase(showOpenSubmissions.rejected, (state, action) => {
        errorToast(action.error.message)
        state.isLoading = false
      })
      .addCase(switchSolution.pending, (state) => {
        state.isLoading = true
      })
      .addCase(switchSolution.fulfilled, (state, action) => {
        const {
          categoryForms,
          categoryFormSubmissions,
          categorySlug,
          conversationsFeatureEnabled,
          reportableFields,
          shownFormSubmissionCounts,
          statusList,
          subscribedToAuthenticatedForms,
          usedForSurvey,
        } = action.payload

        // Survey-related columns belong only to a solution used for surveys.
        //
        // Authenticated forms columns are the opposite: a survey solution's
        // submissions are made through a survey token and never have a submitter,
        // so those columns belong only to a solution used for general forms, and
        // then only when the organization subscribes to authenticated forms.
        const authenticatedFormsColumnsAvailable = !usedForSurvey && subscribedToAuthenticatedForms

        // Conversations columns require both the conversations feature flag and
        // the authenticated forms subscription.
        const conversationsColumnsAvailable = conversationsFeatureEnabled && subscribedToAuthenticatedForms

        const unavailableColumnValues = new Set([
          ...(usedForSurvey ? [] : SURVEY_RELATED_COLUMN_VALUES),
          ...(authenticatedFormsColumnsAvailable ? [] : AUTHENTICATED_FORMS_COLUMN_VALUES),
          ...(conversationsColumnsAvailable ? [] : CONVERSATIONS_COLUMN_VALUES),
        ])

        const attributeColumns = DEFAULT_COLUMNS.filter(({ value }) => !unavailableColumnValues.has(value))

        // reset sort if the current sorting column is unavailable
        if (unavailableColumnValues.has(state.currentSort.column)) {
          state.currentSort = DEFAULT_COLUMN_SORT
        }

        const columns = [...attributeColumns, ...createReportableFieldColumns(reportableFields)]

        state.selectedCategorySlug = categorySlug
        state.categorySubmissionStatuses = statusList.submissionStatuses
        state.forms[categorySlug] = categoryForms
        state.columns = columns

        if (!state.formSubmissions[categorySlug]) {
          state.formSubmissions[categorySlug] = {}
        }

        state.formSubmissions[categorySlug][state.show] = categoryFormSubmissions

        state.columnOrder = columns.reduce((columnOrder, column) => {
          if (!columnOrder.includes(column.value)) {
            columnOrder.push(column.value)
          }

          return columnOrder
        }, state.columnOrder)

        state.formSubmissionCounts[state.show] = shownFormSubmissionCounts

        state.isLoading = false
      })
      .addCase(switchSolution.rejected, (state, action) => {
        errorToast(action.error.message)
        state.isLoading = false
      })
  },
})

incrementPaginationLoadedCount = formSubmissionTableSlice.actions.incrementPaginationLoadedCount
resetPaginationCounts = formSubmissionTableSlice.actions.resetPaginationCounts
setPaginationTotalCount = formSubmissionTableSlice.actions.setPaginationTotalCount

export const {
  clearFormSubmissionCounts,
  clearFormSubmissions,
  hideAllForms,
  hideColumn,
  hideDepartment,
  hideForm,
  reorderColumns,
  showAllDepartmentIds,
  setCategories,
  setColumnOrder,
  setCurrentSort,
  setFacilitiesForFormSubmissions,
  setFormIdsToHide,
  setNoLocationAnswerOption,
  setDepartmentIdsToHide,
  setSelectedGrouping,
  setShow,
  setVisibleColumns,
  showAllForms,
  showColumn,
  showDepartment,
  showForm,
  sortByColumn,
  toggleFacilityVisibility,
  hideAllDepartmentIds,
} = formSubmissionTableSlice.actions

export const getCurrentSort = (state) => (
  {
    ...state[SLICE_NAME].currentSort,
    column: state[SLICE_NAME].columns.find((column) => (
      column.value === state[SLICE_NAME].currentSort.column
    )),
  }
)

export const getDepartmentIdsToHide = (state) => state[SLICE_NAME].departmentIdsToHide

export const getFormSubmissionTable = (state) => state[SLICE_NAME]

export const getFormIdsToHide = (state) => state[SLICE_NAME].formIdsToHide

export const getFacilityIdMap = (state) => state[SLICE_NAME].facilityIdMap

export const getCategories = (state) => state[SLICE_NAME].categories

export const getNoLocationAnswerOptionChecked = (state) => state[SLICE_NAME].noLocationAnswerOption

export const getSelectedCategorySlug = (state) => state[SLICE_NAME].selectedCategorySlug

const getForms = (state) => state[SLICE_NAME].forms

export const getCategoryForms = (state) => {
  const { selectedCategorySlug } = state[SLICE_NAME]

  return getForms(state)[selectedCategorySlug] ?? []
}

export const getSelectedDepartmentIds = (state) => state[SLICE_NAME].selectedDepartmentIds

export const formSubmissionCountsByCategory = (state) => {
  const { formSubmissionCounts, show } = state[SLICE_NAME]

  return formSubmissionCounts[show] ?? {}
}

export const shownDepartmentCountsByFacility = (state) => {
  const { facilityIdMap = {}, departmentIdsToHide = [] } = state[SLICE_NAME] || {}
  const hiddenSet = new Set(departmentIdsToHide)

  return Object.values(facilityIdMap).reduce((counts, facility) => {
    const total = (facility.departments || []).length
    const visible = (facility.departments || []).filter(
      (dept) => !hiddenSet.has(dept.id),
    ).length

    counts[facility.id] = { visible, total }
    return counts
  }, {})
}

export const shownFormSubmissionCountsByForm = (state) => {
  const { formSubmissions, selectedCategorySlug, show } = state[SLICE_NAME]

  const forms = getCategoryForms(state)

  const formCounts = forms.reduce((counts, form) => {
    counts[form.id] = 0

    return counts
  }, {})

  const shownFormSubmissions = formSubmissions[selectedCategorySlug]?.[show] ?? []

  return shownFormSubmissions.reduce((counts, formSubmission) => {
    const { form } = formSubmission
    counts[form.id] += 1

    return counts
  }, formCounts)
}

export const getIsLoading = (state) => state[SLICE_NAME].isLoading

export const getOrderedColumns = (state) => (
  sortBy(
    state[SLICE_NAME].columns,
    [
      (column) => state[SLICE_NAME].columnOrder.indexOf(column.value),
    ],
  )
)

export const getVisibleColumns = (state) => {
  const { columns, columnOrder, visibleColumns } = state[SLICE_NAME]

  return sortBy(
    columns.filter((column) => visibleColumns.includes(column.value)),
    [
      (column) => columnOrder.indexOf(column.value),
    ],
  )
}

export const getFilteredSortedGroupedSubmissions = (state) => {
  const {
    categorySubmissionStatuses,
    columns,
    currentSort,
    departmentIdsToHide,
    formIdsToHide,
    formSubmissions,
    groupBy,
    noLocationAnswerOption,
    selectedCategorySlug,
    show,
  } = state[SLICE_NAME]
  const formSubmissionsToShow = formSubmissions[selectedCategorySlug]?.[show] ?? []

  // filter by forms
  let filteredSubmissions = formSubmissionsToShow.filter(({ form }) => !formIdsToHide.includes(form.id))

  // Filter by submission status
  if (show === SHOW_CLOSED_SUBMISSIONS) {
    filteredSubmissions = filteredSubmissions.filter(({ isClosed }) => isClosed)
  } else if (show === SHOW_DISCARDED_SUBMISSIONS) {
    filteredSubmissions = filteredSubmissions.filter(({ isDeleted }) => isDeleted)
  } else {
    filteredSubmissions = filteredSubmissions.filter(({ isOpen }) => isOpen)
  }

  // Filter by location answer options
  const hiddenDeptSet = new Set(departmentIdsToHide)

  filteredSubmissions = filteredSubmissions.filter((submission) => (
    (noLocationAnswerOption && !submission.answerDepartmentIds?.length)
    || submission.answerDepartmentIds?.some((id) => !hiddenDeptSet.has(id))
  ))

  const sortColumn = columns.find((column) => column.value === currentSort.column)

  const dataSortingMethod = getDataSortingMethod(sortColumn)
  filteredSubmissions = dataSortingMethod(
    filteredSubmissions,
    {
      column: sortColumn,
      isAsc: currentSort.isAsc,
    },
  )

  const groupingMethod = getGroupingMethod({ value: groupBy })
  return groupingMethod(filteredSubmissions, { categorySubmissionStatuses })
}

export const showingClosedFormSubmissions = (state) => (
  state[SLICE_NAME].show === SHOW_CLOSED_SUBMISSIONS
)

export const showingDiscardedFormSubmissions = (state) => (
  state[SLICE_NAME].show === SHOW_DISCARDED_SUBMISSIONS
)

export const paginationFormSubmissionLoadedCount = (state) => (
  state[SLICE_NAME].paginationLoadedCount
)

export const paginationFormSubmissionTotalCount = (state) => (
  state[SLICE_NAME].paginationTotalCount
)

export default formSubmissionTableSlice.reducer
